import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight, Clock, MapPin, Eye } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const OpportunitiesList = ({ deals = [], title = 'Opportunities', maxItems = 5, showMyDeals = false }) => {
  const { userTheme } = useTheme();
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const items = deals.slice(0, maxItems);

  useEffect(() => {
    if (!scrollRef.current || items.length <= 3 || isPaused) return;

    const container = scrollRef.current;
    let animationId;

    const scroll = () => {
      if (!isPaused && container) {
        container.scrollTop += 0.4;
        if (container.scrollTop >= container.scrollHeight - container.clientHeight) {
          container.scrollTop = 0;
        }
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [items.length, isPaused]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - d) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-gray-100" style={{ backgroundColor: userTheme?.dashboardWidgetHeader?.backgroundColor }}>
        <div className="flex items-center gap-2 min-w-0">
          <Package className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-gray-800 truncate">{title}</h3>
          {items.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0">
              {items.length}
            </span>
          )}
        </div>
        <Link
          to="/admin/deals"
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 flex-shrink-0 ml-2"
        >
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* List */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-hidden max-h-[280px] sm:max-h-[320px]"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {items.length === 0 ? (
          <div className="p-5 text-center">
            <p className="text-sm text-gray-500">
              {showMyDeals ? 'No deals posted yet.' : 'No opportunities available.'}
            </p>
            {showMyDeals && (
              <Link
                to="/admin/deals"
                className="inline-flex items-center gap-1 text-sm text-blue-600 mt-2 font-medium"
              >
                Post your first deal <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((deal, idx) => (
              <Link
                key={deal._id || deal.id || idx}
                to={`/admin/deals`}
                className="block px-4 sm:px-5 py-3 hover:bg-gray-50 transition"
              >
                <div className="flex items-center justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {deal.title}
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
                      {deal.category_name && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {deal.category_name}
                        </span>
                      )}
                      {deal.country && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {deal.country}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDate(deal.created_at)}
                    </span>
                    {showMyDeals && deal.views !== undefined && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {deal.views || 0}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunitiesList;
